import {
  describeValue,
  isEnumKind,
  isMarkerKind,
  type CheckInTemplate,
  type Entry,
  type Tag,
  type Track,
} from '@anytracker/core'
import { Link } from 'react-router-dom'

import { checkInColour, paletteVar, trackFill } from '../../charts/colours.js'
import { ICONS, Icon, Swatch } from '../../components/Layout.js'
import { formatTime } from '../../format/dates.js'
import styles from './Timeline.module.css'

interface Props {
  readonly entry: Entry
  /** Every track, archived included — see below. */
  readonly tracks: ReadonlyMap<string, Track>
  readonly tags: ReadonlyMap<string, Tag>
  readonly templates: ReadonlyMap<string, CheckInTemplate>
  readonly deviceZone: string
  /** The last entry of its day, which closes the day's band. */
  readonly last: boolean
}

/**
 * One recorded moment, as a row in its day's band (T-305, Meadow redesign).
 *
 * `tracks` must contain **archived tracks too**. Resolving against the active
 * list only is how history silently develops holes as tracks are retired — the
 * measurement is still there, and it renders as nothing at all.
 *
 * The time leads in its own column, so a day's entries line up and the shape of
 * the day is visible without reading any of them. The check-in's colour marks
 * the name: a convention for scanning, never a meaning — the name sits beside
 * it.
 */
export function EntryRow({ entry, tracks, tags, templates, deviceZone, last }: Props) {
  const template =
    entry.templateId === undefined ? undefined : templates.get(entry.templateId)
  // Only worth saying when it differs — noting "Europe/London" on every entry
  // for someone who never leaves London is noise.
  const elsewhere = entry.at.timeZone !== deviceZone
  const mark =
    template === undefined
      ? 'var(--border-strong)'
      : paletteVar(checkInColour(template), 'fill')

  return (
    <Link
      to={`/timeline/${entry.id}`}
      className={`${styles['entry']} ${last ? styles['entryLast'] : ''}`}
    >
      <span className={styles['entryTime']}>{formatTime(entry.at)}</span>

      <span className={styles['entryMain']}>
        <span className={styles['entryHeader']}>
          <span className={styles['entryName']}>
            <Swatch colour={mark} size={8} />
            {template?.name ?? 'Entry'}
          </span>
          {elsewhere && <span className={styles['entryZone']}>{entry.at.timeZone}</span>}
        </span>

        <span className={styles['measurements']}>
          {entry.measurements.map((measurement) => {
            const track = tracks.get(measurement.trackId)
            if (!track) {
              // The track was hard-deleted, which only happens when it had no
              // measurements — so this should be unreachable. Rendering the
              // value beats rendering nothing if it ever is not.
              return (
                <span key={measurement.trackId} className={styles['measurement']}>
                  <span className={styles['value']}>{String(measurement.value)}</span>
                </span>
              )
            }

            const optionColour =
              isEnumKind(track.kind) && typeof measurement.value === 'string'
                ? track.kind.options.find((option) => option.id === measurement.value)
                    ?.colour
                : undefined

            return (
              <span
                key={measurement.trackId}
                className={`${styles['measurement']} ${track.archived ? styles['archivedTrack'] : ''}`}
              >
                <Swatch colour={optionColour ?? trackFill(track)} size={6} />
                {track.name}
                {/*
                  A marker shows its name and nothing else: the name *is* what
                  happened, and "Drank Recorded" says it twice.
                */}
                {!isMarkerKind(track.kind) && (
                  <>
                    {' '}
                    <span className={styles['value']}>
                      {/* Resolved through the track's own configuration, so a
                          boolean reads as its label and an enum as its option —
                          including one archived since. */}
                      {describeValue(track, measurement.value) ??
                        String(measurement.value)}
                    </span>
                    {track.unit !== undefined && <span>{track.unit}</span>}
                  </>
                )}
              </span>
            )
          })}
        </span>

        {entry.tagIds.length > 0 && (
          <span className={styles['entryTags']}>
            {entry.tagIds.map((tagId) => {
              const tag = tags.get(tagId)
              if (!tag) {
                return null
              }
              return (
                <span key={tagId} className={styles['tag']}>
                  {tag.colour !== undefined && <Swatch colour={tag.colour} size={6} />}
                  {tag.name}
                </span>
              )
            })}
          </span>
        )}

        {entry.note !== undefined && (
          <span className={styles['entryNote']}>{entry.note}</span>
        )}
      </span>

      <Icon path={ICONS.chevronRight} className={styles['entryChevron']} />
    </Link>
  )
}
